import React from "react";
import { ArrowLeft, Calendar, User, Tag, Share2, Bookmark } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: {
  getCurrentWPQueryParams: () => Record<string, unknown>;
};

function PostDetailConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl py-12">
        <div className="h-8 w-32 bg-muted/60 animate-pulse rounded-md mb-6" />
        <div className="h-12 w-3/4 bg-muted/60 animate-pulse rounded-md mb-4" />
        <div className="h-5 w-1/2 bg-muted/60 animate-pulse rounded-md mb-8" />
        <div className="h-[420px] w-full bg-muted/60 animate-pulse rounded-md mb-8" />
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted/60 animate-pulse rounded-md" />
          <div className="h-4 w-5/6 bg-muted/60 animate-pulse rounded-md" />
          <div className="h-4 w-4/6 bg-muted/60 animate-pulse rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <p className="text-destructive font-medium mb-4">
          Không thể tải nội dung bài viết vào lúc này.
        </p>
        <Button variant="outline" onClick={() => refetch()}>
          Thử lại
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <p className="text-muted-foreground mb-4">
          Không tìm thấy bài viết được yêu cầu.
        </p>
        <Button asChild variant="default">
          <Link to="/NewsAndEventsPage">
            Quay lại danh mục tin tức
          </Link>
        </Button>
      </div>
    );
  }

  const post = posts[0];
  const postCategory = post.categories && post.categories.length > 0 ? post.categories[0].name : "Tin tức & Sự kiện";
  const authorName = post.author?.name || "Ban Thông tin VAEG";
  const publishDate = post.date || "Cập nhật gần đây";

  return (
    <article className="mx-auto max-w-4xl">
      {/* Navigation and Category Eyebrow */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
          <Link to="/NewsAndEventsPage">
            <ArrowLeft className="size-4 mr-2" />
            Về chuyên mục tin tức
          </Link>
        </Button>

        <span className="inline-flex items-center px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-accent text-accent-foreground rounded-sm border border-border">
          {postCategory}
        </span>
      </div>

      {/* Title */}
      <h1 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-secondary mb-6 leading-tight">
        {post.title}
      </h1>

      {/* Metadata Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-y border-border py-4 mb-8">
        <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <User className="size-4 text-primary" />
            <span className="font-medium text-foreground">{authorName}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="size-4 text-primary" />
            <span>{publishDate}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground">
            <Share2 className="size-3.5" />
            Chia sẻ
          </Button>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground">
            <Bookmark className="size-3.5" />
            Lưu
          </Button>
        </div>
      </div>

      {/* Featured Image */}
      {post.featuredImage && (
        <figure className="mb-10 overflow-hidden rounded-md border border-border bg-muted">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-auto max-h-[520px] object-cover"
          />
        </figure>
      )}

      {/* Post Content */}
      {post.content ? (
        <div
          className="prose prose-neutral max-w-none text-foreground leading-relaxed space-y-6 text-base md:text-lg"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      ) : (
        <div className="text-foreground leading-relaxed space-y-6 text-base md:text-lg">
          {post.excerpt && <p className="font-medium text-lg md:text-xl text-foreground/90">{post.excerpt}</p>}
          <p>
            Hội Địa lý môi trường Việt Nam (VAEG) là tổ chức xã hội - nghề nghiệp hoạt động tuân thủ theo
            Quyết định số 483/QĐ-BNV và Điều lệ phê duyệt tại Quyết định số 1157/QĐ-BNV của Bộ Nội vụ. Các thông tin
            nghiên cứu, sự kiện hội thảo và chương trình khảo sát môi trường địa lý luôn được Hội cập nhật đầy đủ,
            chính xác trên cổng thông tin điện tử chính thức.
          </p>
        </div>
      )}

      {/* Tags Section */}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-10 pt-6 border-t border-border flex flex-wrap items-center gap-2">
          <Tag className="size-4 text-muted-foreground mr-2" />
          {post.tags.map((tag, i) => (
            <span
              key={tag.id || i}
              data-index={i}
              className="inline-block px-3 py-1 text-xs rounded-sm bg-muted text-muted-foreground font-medium"
            >
              #{tag.name}
            </span>
          ))}
        </div>
      )}

      <Separator className="my-10" />

      {/* Author Card */}
      <Card className="border border-border bg-card shadow-xs">
        <CardContent className="p-6 md:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <Avatar className="size-16 border border-border">
              {post.author?.avatar?.["96"] ? (
                <AvatarImage src={post.author.avatar["96"]} alt={authorName} />
              ) : null}
              <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-lg">
                {authorName.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-1.5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Tác giả biên tập</p>
              <h3 className="text-lg font-bold text-foreground">{authorName}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Ban Truyền thông & Nghiên cứu khoa học thuộc Hội Địa lý môi trường Việt Nam. Phụ trách tổng hợp thông tin, công bố hoạt động học thuật và thông báo định kỳ của Hội.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

export default function Article() {
  const currentWPQueryParams = typeof wvcClient !== "undefined" && wvcClient.getCurrentWPQueryParams
    ? wvcClient.getCurrentWPQueryParams()
    : { post_type: "post", posts_per_page: 1 };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-post-template-section"
      data-nav="light"
      className="bg-background text-foreground py-12 md:py-16 px-4 sm:px-6 lg:px-8 border-b border-border"
    >
      <div className="mx-auto max-w-7xl">
        <PostsProvider wp_query={wp_query}>
          <PostDetailConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}